import { useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  FileText,
  ArrowLeftRight,
  Warehouse,
  ScanBarcode,
  BarChart3,
  Menu,
  X,
  LogOut,
  User,
} from "lucide-react";
import { StockCheckLogo } from "./Logo";
import { toast } from "sonner";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/products", icon: Package, label: "Products" },
  { path: "/invoices", icon: FileText, label: "Invoices" },
  { path: "/transactions", icon: ArrowLeftRight, label: "Transactions" },
  { path: "/warehouses", icon: Warehouse, label: "Warehouses" },
  { path: "/reports", icon: BarChart3, label: "Reports" },
  { path: "/scanner", icon: ScanBarcode, label: "Scanner" },
];

export const Logo = () => <StockCheckLogo size="default" showText={true} />;

// Get user from localStorage synchronously on initial render
const getInitialUser = () => {
  try {
    const userData = localStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  } catch {
    return null;
  }
};

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(getInitialUser);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    toast.success("Logged out successfully");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 bg-[#0B0E14] border-r border-slate-800 h-screen fixed left-0 top-0 z-40 flex-col">
        <div className="p-6 border-b border-slate-800">
          <Logo />
        </div>
        <nav className="flex-1 py-4 px-3">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) =>
                `sidebar-link flex items-center gap-3 px-4 py-3 rounded-sm mb-1 text-sm font-medium ${
                  isActive
                    ? "active bg-blue-500/10 text-blue-400 border-r-2 border-blue-500"
                    : "text-slate-400 hover:text-white"
                }`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          {user && (
            <div className="mb-3 px-2">
              <div className="flex items-center gap-2 mb-2">
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{user.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user.email}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          )}
          <p className="text-xs text-slate-600 text-center">
            Inventory Control Room
          </p>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-[#0B0E14] border-b border-slate-800 z-50 px-4 py-3 flex items-center justify-between">
        <Logo />
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 text-slate-400 hover:text-white"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`md:hidden fixed top-0 left-0 h-full w-64 bg-[#0B0E14] border-r border-slate-800 z-50 transform transition-transform ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-6 border-b border-slate-800">
          <Logo />
        </div>
        <nav className="py-4 px-3">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `sidebar-link flex items-center gap-3 px-4 py-3 rounded-sm mb-1 text-sm font-medium ${
                  isActive
                    ? "active bg-blue-500/10 text-blue-400"
                    : "text-slate-400 hover:text-white"
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#151923] border-t border-slate-800 p-2 z-50 flex justify-around">
        {navItems.slice(0, 6).map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === "/"}
            className={({ isActive }) =>
              `mobile-nav-item flex flex-col items-center gap-1 p-1 ${
                isActive ? "active text-blue-400" : "text-slate-500"
              }`
            }
          >
            <item.icon className="w-4 h-4" />
            <span className="text-[10px]">{item.label.slice(0, 5)}</span>
          </NavLink>
        ))}
      </nav>

      {/* Main Content */}
      <main className="md:pl-64 min-h-screen bg-[#0B0E14] pt-16 md:pt-0 pb-20 md:pb-0">
        <div className="grid-background absolute inset-0 pointer-events-none opacity-30" />
        <div className="relative p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
